import type { CompanyProductRepository } from "../../../domain/repositories/company/company-product-repository";
import { companyProductApiService } from "../../api/company/company-product-api-service";
import type {
  CreateProductRequest,
  DetailPageDto,
  LinkProductOptionsRequest,
  ProductDto,
  ProductManagementDto,
} from "../../../domain/types/company/company-product";
import type { UpdateProductRequest } from "../../../shared/types/api";

export class CompanyProductRepositoryImpl implements CompanyProductRepository {
  async getProducts(companyId: number): Promise<ProductManagementDto | ProductDto[]> {
    const response = await companyProductApiService.getProducts(companyId);
    if (!response.data) {
      throw new Error("상품 데이터를 가져올 수 없습니다.");
    }
    return response.data;
  }

  async getProduct(companyId: number, productId: number): Promise<ProductDto> {
    const response = await companyProductApiService.getProduct(
      companyId,
      productId,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상품 정보를 가져올 수 없습니다.");
  }

  async createProduct(
    companyId: number,
    request: CreateProductRequest,
    thumbnailFile?: File | null,
    detailPageFiles?: File[] | null,
  ): Promise<ProductDto> {
    const response = await companyProductApiService.createProduct(
      companyId,
      request,
      thumbnailFile,
      detailPageFiles,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상품 생성에 실패했습니다.");
  }

  async updateProduct(
    companyId: number,
    productId: number,
    request: UpdateProductRequest,
    thumbnailFile?: File | null,
    detailPageFiles?: File[] | null,
  ): Promise<ProductDto> {
    const response = await companyProductApiService.updateProduct(
      companyId,
      productId,
      request,
      thumbnailFile,
      detailPageFiles,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상품 수정에 실패했습니다.");
  }

  async deleteProduct(companyId: number, productId: number): Promise<boolean> {
    const response = await companyProductApiService.deleteProduct(
      companyId,
      productId,
    );

    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상품 삭제에 실패했습니다.");
  }

  async linkProductOptions(
    companyId: number,
    productId: number,
    request: LinkProductOptionsRequest,
  ): Promise<ProductDto> {
    const response = await companyProductApiService.linkProductOptions(
      companyId,
      productId,
      request,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상품 옵션 연결에 실패했습니다.");
  }

  async unlinkProductOption(
    companyId: number,
    productId: number,
    productOptionId: number,
  ): Promise<ProductDto> {
    const response = await companyProductApiService.unlinkProductOptions(
      companyId,
      productId,
      productOptionId,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상품 옵션 연결 해제에 실패했습니다.");
  }

  async getDetailPages(
    companyId: number,
    productId: number,
  ): Promise<DetailPageDto[]> {
    const response = await companyProductApiService.getDetailPages(
      companyId,
      productId,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(
      response.errorMsg || "상세 페이지 정보를 가져올 수 없습니다.",
    );
  }

  async addDetailPages(
    companyId: number,
    productId: number,
    files: File[],
  ): Promise<DetailPageDto[]> {
    const response = await companyProductApiService.addDetailPages(
      companyId,
      productId,
      files,
    );

    if (response.data) {
      return [response.data];
    }

    throw new Error(response.errorMsg || "상세 페이지 추가에 실패했습니다.");
  }

  async insertDetailPageAt(
    companyId: number,
    productId: number,
    file: File,
    afterDetailPageId: number,
  ): Promise<DetailPageDto[]> {
    const response = await companyProductApiService.insertDetailPageAt(
      companyId,
      productId,
      file,
      afterDetailPageId,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상세 페이지 교체에 실패했습니다.");
  }

  async replaceDetailPages(
    companyId: number,
    productId: number,
    files: File[],
  ): Promise<DetailPageDto[]> {
    const response = await companyProductApiService.replaceDetailPages(
      companyId,
      productId,
      files,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상세 페이지 교체에 실패했습니다.");
  }

  async updateDetailPageOrders(
    companyId: number,
    productId: number,
    detailPageIds: number[],
  ): Promise<DetailPageDto[]> {
    const response = await companyProductApiService.updateDetailPageOrders(
      companyId,
      productId,
      detailPageIds,
    );

    if (response.data) {
      return response.data;
    }

    throw new Error(
      response.errorMsg || "상세 페이지 순서 변경에 실패했습니다.",
    );
  }

  async deleteDetailPage(
    companyId: number,
    productId: number,
    detailPageId: number,
  ): Promise<boolean> {
    const response = await companyProductApiService.deleteDetailPage(
      companyId,
      productId,
      detailPageId,
    );

    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }

    throw new Error(response.errorMsg || "상세 페이지 삭제에 실패했습니다.");
  }

  async deleteAllDetailPages(
    companyId: number,
    productId: number,
  ): Promise<boolean> {
    const response = await companyProductApiService.deleteAllDetailPages(
      companyId,
      productId,
    );

    if (response.data !== null && response.data !== undefined) {
      return response.data;
    }

    throw new Error(
      response.errorMsg || "모든 상세 페이지 삭제에 실패했습니다.",
    );
  }
}
